import { graphql, useFragment } from "react-relay";
import { TimeSeriesTable$key } from "./__generated__/TimeSeriesTable.graphql";
import { AHSeparator, AHText, Box, ListItem, TouchableBox } from "arkh";

import React, { useMemo, useState, useTransition } from "react";

import { MultiGrid } from "react-virtualized";

import { useFormatValueUnit } from "./formatDuration";

import { AnalyticsValueUnit } from "./__generated__/TimeSeriesOverviewItem_data.graphql";
import { OrgSearchInput } from "../OrgSearchInput";
import ParentSize from "@visx/responsive/lib/components/ParentSize";

const CELL_WIDTH = 70;
const CELL_HEIGHT = 40;
const FIRST_COLUMN_WIDTH = 180;

export function TimeSeriesTable(props: {
  data: TimeSeriesTable$key;

  activateDimensions: (dimensionIds: string[]) => void;
  deactivateDimensions: (dimensionIds: string[]) => void;
  selectedDimensions: string[];
  setHoverDimension: (dimensionId: string | undefined) => void;
  hoverDimension: string | null;
  unit?: AnalyticsValueUnit;
  dimensionLabel?: React.ReactNode;
}) {
  const data = useFragment(
    graphql`
      fragment TimeSeriesTable on TimeSeriesAnalyticsConnection {
        dimensions {
          id
          label
          color
        }
        edges {
          node {
            formattedDate
            values {
              dimensionId
              value
            }
          }
        }
      }
    `,
    props.data
  );

  const checkboxStatus = useMemo(() => {
    if (props.selectedDimensions.length === 0) {
      return false;
    }

    if (props.selectedDimensions.length === data.dimensions.length) {
      return true;
    }
    return "indeterminate";
  }, [props.selectedDimensions, data.dimensions]);

  const [search, setSearch] = useState("");

  const [, enterSearch] = useTransition();

  const filteredDimensions = useMemo(() => {
    return data.dimensions.filter((d) =>
      d.label.toLowerCase().includes(search.toLowerCase())
    );
  }, [data.dimensions, search]);
  const allDimensionIds = useMemo(
    () => filteredDimensions.map((d) => d.id),
    [filteredDimensions]
  );

  const allSelectedDimensionIds = useMemo(
    () =>
      props.selectedDimensions.filter((dId) => allDimensionIds.includes(dId)),
    [props.selectedDimensions, allDimensionIds]
  );

  const checkboxIntentStatus = useMemo(() => {
    if (
      allSelectedDimensionIds.length === allDimensionIds.length &&
      allSelectedDimensionIds.every((id) => allDimensionIds.includes(id))
    ) {
      return true;
    }
    return false;
  }, [allSelectedDimensionIds, allDimensionIds]);

  const formatValue = useFormatValueUnit();

  return (
    <>
      <OrgSearchInput
        value={search}
        onChangeText={(e) => {
          enterSearch(() => {
            setSearch(e);
          });
        }}
        mode="integrated"
      />
      <AHSeparator />
      <ParentSize debounceTime={10}>
        {(size) => (
          <MultiGrid
            rowCount={filteredDimensions.length + 1}
            columnCount={data.edges.length + 1}
            scrollToColumn={data.edges.length}
            columnWidth={(index) => {
              if (index.index === 0) {
                return FIRST_COLUMN_WIDTH;
              }
              return CELL_WIDTH;
            }}
            height={size.height}
            width={size.width}
            enableFixedColumnScroll
            enableFixedRowScroll
            fixedColumnCount={1}
            fixedRowCount={1}
            rowHeight={CELL_HEIGHT}
            hideTopRightGridScrollbar
            hideBottomLeftGridScrollbar
            cellRenderer={({ columnIndex, rowIndex, style: s }) => {
              const timeSeriesPoint =
                data.edges[data.edges.length - columnIndex];

              if (rowIndex === 0) {
                if (columnIndex === 0) {
                  return (
                    <div
                      style={{
                        ...s,

                        display: "flex",
                        flexDirection: "row" as const,
                        alignItems: "center",
                      }}
                    >
                      <TouchableBox
                        spaceLeft="medium"
                        style={{
                          flexDirection: "row",
                          alignItems: "center",
                          flex: 1,
                          borderBottomWidth: 1,
                          height: "100%",
                        }}
                        borderColor="border-primary"
                        onPress={() => {
                          if (checkboxIntentStatus === false) {
                            props.activateDimensions(allDimensionIds);
                          } else {
                            props.deactivateDimensions(allDimensionIds);
                          }
                        }}
                      >
                        <ListItem.MultiSelectIndicator
                          selected={checkboxStatus}
                        />
                        <Box
                          style={{
                            flexDirection: "row",
                            alignItems: "baseline",
                          }}
                        >
                          <AHText
                            spaceLeft="small"
                            numberOfLines={1}
                            type="subtitle"
                          >
                            {props.dimensionLabel}
                          </AHText>
                          <AHText spaceLeft="small" type="detail">
                            ({data.dimensions.length})
                          </AHText>
                        </Box>
                      </TouchableBox>
                      <AHSeparator />
                    </div>
                  );
                }
                return (
                  <div
                    style={{
                      ...s,

                      display: "flex",
                      flexDirection: "row" as const,
                      alignItems: "center",
                    }}
                  >
                    <Box
                      style={{
                        flexDirection: "row",
                        alignItems: "center",
                        flex: 1,
                        borderBottomWidth: 1,
                        height: "100%",
                      }}
                      borderColor="border-primary"
                    >
                      <AHText
                        type="subtitle"
                        numberOfLines={1}
                        size="detail"
                        color="text-secondary"
                      >
                        {timeSeriesPoint?.node?.formattedDate}
                      </AHText>
                    </Box>
                  </div>
                );
              }

              const dimension =
                filteredDimensions[filteredDimensions.length - rowIndex];
              const isHovered = props.hoverDimension === dimension?.id;

              const style = {
                ...s,
                backgroundColor: isHovered ? "#EFEFEF" : "white",
                display: "flex",
                flexDirection: "row" as const,
                alignItems: "center",
              };
              function onMouseEnter() {
                props.setHoverDimension(dimension.id);
              }
              function onMouseLeave() {
                props.setHoverDimension(undefined);
              }

              if (columnIndex === 0) {
                const isSelected = props.selectedDimensions.includes(
                  dimension?.id
                );
                return (
                  <div
                    style={{
                      ...style,
                    }}
                    onMouseEnter={onMouseEnter}
                    onMouseLeave={onMouseLeave}
                  >
                    <TouchableBox
                      spaceLeft="medium"
                      style={{
                        flexDirection: "row",
                        alignItems: "center",
                        flex: 1,
                      }}
                      onPress={() => {
                        if (!isSelected) {
                          props.activateDimensions([dimension?.id]);
                        } else {
                          props.deactivateDimensions([dimension?.id]);
                        }
                      }}
                      borderColor="border-primary"
                    >
                      <ListItem.MultiSelectIndicator
                        selected={isSelected}
                        customTintColor={dimension?.color ?? undefined}
                      />

                      <AHText
                        spaceLeft="small"
                        numberOfLines={1}
                        type="subtitle"
                      >
                        {dimension?.label}
                      </AHText>
                    </TouchableBox>
                  </div>
                );
              }
              const value =
                timeSeriesPoint?.node.values.find(
                  (v) => v.dimensionId === dimension?.id
                )?.value ?? 0;
              return (
                <div
                  style={style}
                  onMouseEnter={onMouseEnter}
                  onMouseLeave={onMouseLeave}
                >
                  <AHText style={{ flex: 1 }} numberOfLines={1}>
                    {formatValue(value, props.unit ?? "unit")}
                  </AHText>
                </div>
              );
            }}
          />
        )}
      </ParentSize>
    </>
  );
}
